import { useState } from "react";
import { toast } from "sonner";
import { ApiService } from "@/services/api/ApiService";
import { Agent } from '@/types/agent/agent';
import type { OutboundCallResult } from '@/types/phone/outbound-call';

/**
 * useOutboundCall — place one real call from a Genie's number.
 *
 * Two callers now:
 *   - the phone drawer's "call me" button, which still asks for a number
 *   - the forwarding verification step, which already knows the number and
 *     needs the result back rather than an alert box
 *
 * So `makeCall` takes an optional target and RETURNS the result. When no
 * target is passed it falls back to asking, which preserves the original
 * behaviour of the drawer button.
 */
export const useOutboundCall = () => {
  const [isCalling, setIsCalling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const makeCall = async (
    genie: Agent,
    elevenlabsPhoneNumberId: string,
    toNumber?: string,
  ): Promise<OutboundCallResult | null> => {
    const target =
      toNumber ?? window.prompt("Enter your verified phone number to test (e.g., +1234567890):");
    if (!target) return null;

    setIsCalling(true);
    setError(null);

    try {
      const result = await ApiService.callEdge<OutboundCallResult>("elevenlabs", {
        resource: "conversations",
        action: "outbound-call",
        params: {
          agent_id: genie.elevenlabs_id,
          agent_phone_number_id: elevenlabsPhoneNumberId,
          to_number: target,
          conversation_initiation_client_data: {
            conversation_config_override: {
              agent: {
                first_message:
                  genie.settings?.conversation_config?.agent?.first_message ||
                  (genie.welcome_message ?? genie.page?.welcome_message) ||
                  "Hello! How can I assist you today?",
              },
            },
          },
        },
      });

      if (result.success) {
        // Only announce it when nobody else is rendering the outcome.
        if (!toNumber) {
          toast.success("Calling you now", {
            description: "Answer to hear exactly what your customers hear.",
          });
        }
      } else {
        setError(result.message || "The call could not be placed.");
      }

      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to place the call";
      setError(message);
      return { success: false, message };
    } finally {
      setIsCalling(false);
    }
  };

  return {
    makeCall,
    isCalling,
    error,
    clearError: () => setError(null),
  };
};
